// src/AttenzioButton.tsx
import { forwardRef } from "react";
import { jsx } from "react/jsx-runtime";
var variantClasses = {
  primary: "bg-[var(--blue-600)] text-white hover:bg-[var(--blue-700)] active:bg-[var(--blue-800)] shadow-sm",
  secondary: "bg-[var(--surface-card)] text-[var(--text-strong)] border border-[var(--border-default)] hover:bg-[var(--surface-hover)] hover:border-[var(--border-strong)]",
  ghost: "bg-transparent text-[var(--text-muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-strong)]",
  danger: "bg-[var(--red-600)] text-white hover:bg-[var(--red-700)] active:bg-[var(--red-800)] shadow-sm"
};
var sizeClasses = {
  sm: "px-3 py-1.5 text-[13px] font-medium rounded-[var(--radius-md)]",
  md: "px-4 py-2 text-[14px] font-medium rounded-[var(--radius-md)]",
  lg: "px-5 py-2.5 text-[15px] font-semibold rounded-[var(--radius-lg)]"
};
var AttenzioButton = forwardRef(
  function AttenzioButton2({
    children,
    variant = "primary",
    size = "md",
    disabled = false,
    onClick,
    className = "",
    type = "button",
    ...rest
  }, ref) {
    return /* @__PURE__ */ jsx(
      "button",
      {
        ref,
        type,
        disabled,
        onClick,
        className: `
        inline-flex items-center justify-center gap-2
        transition-all [transition-duration:var(--dur-base)] [transition-timing-function:var(--ease-standard)]
        disabled:opacity-50 disabled:cursor-not-allowed
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${className}
      `,
        style: { fontFamily: "var(--font-sans)" },
        ...rest,
        children
      }
    );
  }
);

// src/AttenzioBadge.tsx
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var toneStyles = {
  running: {
    bg: "var(--status-running-bg)",
    text: "var(--status-running)",
    dot: "var(--status-running)"
  },
  idle: {
    bg: "var(--status-idle-bg)",
    text: "var(--status-idle)",
    dot: "var(--status-idle)"
  },
  down: {
    bg: "var(--status-down-bg)",
    text: "var(--status-down)",
    dot: "var(--status-down)"
  },
  brand: {
    bg: "var(--brand-subtle)",
    text: "var(--brand)",
    dot: "var(--brand)"
  },
  info: {
    bg: "var(--status-info-bg)",
    text: "var(--status-info)",
    dot: "var(--status-info)"
  },
  neutral: {
    bg: "var(--ink-100)",
    text: "var(--ink-500)",
    dot: "var(--ink-400)"
  }
};
function AttenzioBadge({
  children,
  tone = "neutral",
  size = "sm",
  dot = false,
  pulse = false,
  className = ""
}) {
  const style = toneStyles[tone] || toneStyles.neutral;
  const sizeClass = size === "sm" ? "text-[11px] px-2 py-0.5 font-medium" : "text-[13px] px-2.5 py-1 font-semibold";
  return /* @__PURE__ */ jsxs(
    "span",
    {
      className: `
        inline-flex items-center gap-1.5 rounded-[var(--radius-pill)]
        ${sizeClass}
        ${className}
      `,
      style: {
        backgroundColor: style.bg,
        color: style.text,
        fontFamily: "var(--font-sans)"
      },
      children: [
        dot && /* @__PURE__ */ jsx2(
          "span",
          {
            className: `w-1.5 h-1.5 rounded-full ${pulse ? "animate-pulse" : ""}`,
            style: { backgroundColor: style.dot }
          }
        ),
        children
      ]
    }
  );
}
function AttenzioStatusDot({
  status,
  showLabel = true,
  pulse = false,
  className = ""
}) {
  const labels = {
    running: "Running",
    idle: "Idle",
    down: "Down",
    off: "Off",
    info: "Info"
  };
  const colors = {
    running: "var(--status-running)",
    idle: "var(--status-idle)",
    down: "var(--status-down)",
    off: "var(--ink-400)",
    info: "var(--status-info)"
  };
  return /* @__PURE__ */ jsxs("span", { className: `inline-flex items-center gap-2 ${className}`, children: [
    /* @__PURE__ */ jsx2(
      "span",
      {
        className: `w-2 h-2 rounded-full ${pulse ? "animate-pulse" : ""}`,
        style: { backgroundColor: colors[status] || colors.off }
      }
    ),
    showLabel && /* @__PURE__ */ jsx2(
      "span",
      {
        className: "text-[11px] font-medium uppercase tracking-wider",
        style: { color: colors[status] || colors.off, fontFamily: "var(--font-mono)" },
        children: labels[status]
      }
    )
  ] });
}

// src/AttenzioMetricCard.tsx
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
function AttenzioMetricCard({
  label,
  value,
  unit = "",
  delta,
  trend = "flat",
  caption,
  className = "",
  onClick
}) {
  const trendColor = trend === "up" ? "var(--status-running)" : trend === "down" ? "var(--status-down)" : "var(--text-muted)";
  const TrendIcon = trend === "up" ? TrendingUp : trend === "down" ? TrendingDown : Minus;
  return /* @__PURE__ */ jsxs2(
    "div",
    {
      onClick,
      className: `
        bg-[var(--surface-card)] border border-[var(--border-subtle)]
        rounded-[var(--radius-lg)] shadow-[var(--shadow-sm)]
        p-5 transition-all [transition-duration:var(--dur-base)]
        ${onClick ? "cursor-pointer hover:shadow-[var(--shadow-md)] hover:border-[var(--border-strong)]" : ""}
        ${className}
      `,
      children: [
        /* @__PURE__ */ jsx3(
          "p",
          {
            className: "text-[11px] font-semibold uppercase tracking-wider",
            style: { color: "var(--text-subtle)", fontFamily: "var(--font-mono)" },
            children: label
          }
        ),
        /* @__PURE__ */ jsxs2("div", { className: "flex items-baseline gap-1 mt-2", children: [
          /* @__PURE__ */ jsx3(
            "span",
            {
              className: "text-[32px] font-semibold tracking-tight",
              style: { color: "var(--text-strong)", fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums" },
              children: value
            }
          ),
          unit && /* @__PURE__ */ jsx3("span", { className: "text-[15px] font-medium", style: { color: "var(--text-muted)" }, children: unit })
        ] }),
        delta && /* @__PURE__ */ jsxs2("div", { className: "flex items-center gap-1 mt-1", children: [
          /* @__PURE__ */ jsx3(TrendIcon, { className: "w-3.5 h-3.5", style: { color: trendColor } }),
          /* @__PURE__ */ jsx3("span", { className: "text-[13px] font-medium", style: { color: trendColor }, children: delta })
        ] }),
        /* @__PURE__ */ jsx3("p", { className: "text-[13px] mt-2", style: { color: "var(--text-muted)" }, children: caption })
      ]
    }
  );
}

// src/AttenzioPageHeader.tsx
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
function AttenzioPageHeader({ title, description, children }) {
  return /* @__PURE__ */ jsxs3("div", { className: "flex items-start justify-between gap-4", children: [
    /* @__PURE__ */ jsxs3("div", { className: "min-w-0", children: [
      /* @__PURE__ */ jsx4(
        "h1",
        {
          className: "text-[22px] sm:text-2xl font-extrabold tracking-tight",
          style: { fontFamily: "var(--font-display)", color: "var(--text-strong)" },
          children: title
        }
      ),
      description && /* @__PURE__ */ jsx4("p", { className: "text-sm mt-1", style: { color: "var(--text-muted)" }, children: description })
    ] }),
    children && /* @__PURE__ */ jsx4("div", { className: "flex items-center gap-2 shrink-0", children })
  ] });
}

// src/AttenzioDataPanel.tsx
import { jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
function AttenzioDataPanel({
  children,
  toolbar,
  empty = false,
  emptyTitle = "No results",
  emptyHint,
  emptyAction
}) {
  return /* @__PURE__ */ jsxs4("div", { className: "bg-[var(--surface-card)] border border-[var(--border-subtle)] rounded-[var(--radius-lg)] shadow-[var(--shadow-sm)] overflow-hidden", children: [
    toolbar && /* @__PURE__ */ jsx5("div", { className: "px-4 py-3 border-b border-[var(--border-subtle)]", children: toolbar }),
    empty ? /* @__PURE__ */ jsxs4("div", { className: "px-5 py-12 text-center", children: [
      /* @__PURE__ */ jsx5(
        "p",
        {
          className: "text-[11px] font-semibold uppercase tracking-wider mb-2",
          style: { fontFamily: "var(--font-mono)", color: "var(--text-subtle)" },
          children: "Empty"
        }
      ),
      /* @__PURE__ */ jsx5("p", { className: "text-sm font-medium", style: { color: "var(--text-strong)" }, children: emptyTitle }),
      emptyHint && /* @__PURE__ */ jsx5("p", { className: "text-[13px] mt-1 max-w-md mx-auto", style: { color: "var(--text-muted)" }, children: emptyHint }),
      emptyAction && /* @__PURE__ */ jsx5("div", { className: "mt-4 flex flex-wrap items-center justify-center gap-2", children: emptyAction })
    ] }) : children
  ] });
}

// src/AttenzioTable.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
function AttenzioTable({
  children,
  className = ""
}) {
  return /* @__PURE__ */ jsx6("div", { className: `overflow-x-auto ${className}`, children: /* @__PURE__ */ jsx6("table", { className: "w-full text-sm", children }) });
}
function AttenzioTh({
  children,
  className = "",
  ...props
}) {
  return /* @__PURE__ */ jsx6(
    "th",
    {
      className: `py-3 px-4 text-left text-[11px] font-semibold uppercase tracking-wider border-b border-[var(--border-strong)] text-[var(--text-muted)] ${className}`,
      style: { fontFamily: "var(--font-mono)" },
      ...props,
      children
    }
  );
}
function AttenzioTd({
  children,
  className = "",
  ...props
}) {
  return /* @__PURE__ */ jsx6(
    "td",
    {
      className: `py-3.5 px-4 border-b border-[var(--border-subtle)] text-[var(--text-body)] ${className}`,
      ...props,
      children
    }
  );
}
function AttenzioTr({
  children,
  className = "",
  ...props
}) {
  return /* @__PURE__ */ jsx6(
    "tr",
    {
      className: `hover:bg-[var(--surface-hover)] transition-colors ${className}`,
      ...props,
      children
    }
  );
}

// src/ThemeToggle.tsx
import { Sun, Moon } from "lucide-react";

// src/theme.tsx
import { createContext, useContext, useEffect, useState } from "react";
import { jsx as jsx7 } from "react/jsx-runtime";
var ThemeContext = createContext({
  theme: "system",
  resolvedTheme: "light",
  setTheme: () => {
  },
  toggleTheme: () => {
  }
});
function useTheme() {
  return useContext(ThemeContext);
}
function getSystemTheme() {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
function getResolvedTheme(theme) {
  if (theme === "system") return getSystemTheme();
  return theme;
}
var THEME_STORAGE_KEY = "attenzio-theme";
function readStoredTheme() {
  if (typeof window === "undefined") return "light";
  const next = localStorage.getItem(THEME_STORAGE_KEY);
  if (next) return next;
  const legacy = localStorage.getItem("fsd-theme");
  if (legacy) {
    localStorage.setItem(THEME_STORAGE_KEY, legacy);
    localStorage.removeItem("fsd-theme");
    return legacy;
  }
  return "light";
}
function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => readStoredTheme());
  const [resolvedTheme, setResolvedTheme] = useState(
    () => getResolvedTheme(theme)
  );
  useEffect(() => {
    const resolved = getResolvedTheme(theme);
    setResolvedTheme(resolved);
    localStorage.setItem(THEME_STORAGE_KEY, theme);
    const root = document.documentElement;
    if (resolved === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, [theme]);
  useEffect(() => {
    if (theme !== "system") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => {
      setResolvedTheme(e.matches ? "dark" : "light");
      const root = document.documentElement;
      if (e.matches) root.classList.add("dark");
      else root.classList.remove("dark");
    };
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [theme]);
  const setTheme = (t) => setThemeState(t);
  const toggleTheme = () => {
    setThemeState((prev) => {
      const resolved = getResolvedTheme(prev);
      return resolved === "dark" ? "light" : "dark";
    });
  };
  return /* @__PURE__ */ jsx7(ThemeContext.Provider, { value: { theme, resolvedTheme, setTheme, toggleTheme }, children });
}

// src/ThemeToggle.tsx
import { Fragment, jsx as jsx8, jsxs as jsxs5 } from "react/jsx-runtime";
function ThemeToggle({ className = "" }) {
  const { resolvedTheme, toggleTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  return /* @__PURE__ */ jsx8(
    "button",
    {
      onClick: toggleTheme,
      className: `
        inline-flex items-center gap-2
        px-3 py-2 rounded-[var(--radius-md)]
        text-[13px] font-medium
        transition-all [transition-duration:var(--dur-base)]
        hover:bg-[var(--surface-hover)]
        ${className}
      `,
      style: { color: "var(--text-muted)" },
      title: isDark ? "Switch to light mode" : "Switch to dark mode",
      children: isDark ? /* @__PURE__ */ jsxs5(Fragment, { children: [
        /* @__PURE__ */ jsx8(Sun, { className: "w-4 h-4" }),
        /* @__PURE__ */ jsx8("span", { children: "Light" })
      ] }) : /* @__PURE__ */ jsxs5(Fragment, { children: [
        /* @__PURE__ */ jsx8(Moon, { className: "w-4 h-4" }),
        /* @__PURE__ */ jsx8("span", { children: "Dark" })
      ] })
    }
  );
}
export {
  AttenzioBadge,
  AttenzioButton,
  AttenzioDataPanel,
  AttenzioMetricCard,
  AttenzioPageHeader,
  AttenzioStatusDot,
  AttenzioTable,
  AttenzioTd,
  AttenzioTh,
  AttenzioTr,
  ThemeProvider,
  ThemeToggle,
  useTheme
};
//# sourceMappingURL=index.js.map